import React from 'react';
import { observer } from 'mobx-react';
import { withNamespaces, WithNamespaces, Trans } from 'react-i18next';
import { getDroppedOrSelectedFiles } from 'html5-file-selector';

import { Button } from 'components';
import { FilesTab } from 'apps/repository/views/files';
import { SyncProjectContainer } from 'apps/repository/syncRepository/SyncProjectContainer';
import { HistorySection } from 'apps/repository/views/historySection';
import { Section } from 'legacyComponents/layout/section';
import { openInElectronApp } from 'libs/deeplink';
import { isInDesktopMode } from 'libs/gutClient/electronBridge';

import { Center } from 'legacyComponents/styled/lib';
import { ButtonsWrapper, InputFileStyled } from 'apps/repository/styles';

import { themes } from 'styles/themes/wikifactoryNew';
import { routerStore } from 'core/routing';
import { DownloadAppDialog } from './views/downloadAppDialog';

interface RepositoryProps extends Partial<WithNamespaces> {
  title: string;
  projectManager: Record<string, any>;
  repoManager: Record<string, any>;
  returnLink: Record<string, any>;
  crumbs?: React.ReactNode;
  showHistory?: boolean;
}

@withNamespaces()
@observer
export class Repository extends React.Component<RepositoryProps> {
  fileInput: HTMLInputElement;
  folderInput: HTMLInputElement;

  addFiles = (e: any) => {
    const { repoManager } = this.props;
    getDroppedOrSelectedFiles(e).then(list => {
      if (list.length) repoManager.AddFiles(list);
    });
    this.fileInput.value = '';
    this.folderInput.value = '';
  };

  componentDidCatch = (error: any, info: any) => {
    const { repoManager } = this.props;
    repoManager.CatchError(error, info);
  };

  render() {
    const {
      t,
      title = '',
      projectManager,
      returnLink,
      repoManager,
      crumbs = null,
      showHistory = false,
    } = this.props;
    const { project } = projectManager;

    let subTitle = repoManager.variables.version
      ? `(#${repoManager.variables.version})`
      : '';

    const {
      hasError,
      repository,
      atHead,
      opQueue,
      setDownloadDialogVisible,
      showDownloadAppDialog,
    } = repoManager;

    if (!isInDesktopMode() && (!project || !repository)) {
      return (
        <React.Fragment>
          <Section title={title} subTitle={t(subTitle)}>
            {crumbs}
            <FilesTab
              condition={!hasError}
              app={repository}
              project={project}
              manager={repoManager}
              returnLink={returnLink}
            />
          </Section>
        </React.Fragment>
      );
    }

    const { SetFolderInputVisibility } = repoManager;
    const {
      inSpace: { whichTypes },
    } = project;

    const canContribute = whichTypes.includes('Contribution') && atHead;

    if (atHead) {
      subTitle = '(Latest)';

      const pendingCount = repository?.pendingOperations.length;
      let operationsCount = repository?.pendingOperations.length;
      if (opQueue.localOperations.size) {
        operationsCount = opQueue.localOperations.size;
      }
      if (pendingCount < operationsCount) {
        subTitle = `(${pendingCount} / ${operationsCount} local changes)`;
      } else if (pendingCount >= operationsCount && pendingCount > 0) {
        subTitle = `(${pendingCount} local changes)`;
      }
    }

    const runOnChange = (event: any) => {
      this.addFiles(event);
    };

    const Buttons = canContribute && (
      <ButtonsWrapper nextToCrumbs={crumbs}>
        {
          <DownloadAppDialog
            visible={showDownloadAppDialog}
            onClose={() => {
              setDownloadDialogVisible(false);
            }}
          />
        }
        <Button
          text={t('Upload file')}
          appearance='plain'
          size='small'
          htmlFor='upload_file'
          data-testid='upload-file-icon-button'
          as='label'
          icon='upload'
        />
        <Button
          text={t('New folder')}
          size='small'
          appearance='plain'
          data-testid='new-folder-button'
          onClick={() => SetFolderInputVisibility(true)}
          icon='folder_add'
        />
        <Button
          text={t('Upload folder')}
          appearance='plain'
          size='small'
          data-testid='upload-folder-icon-button'
          htmlFor='upload_folder'
          as='label'
          icon='folder'
        />

        <Button
          text={
            <Trans>
              <span className='font-bold'>Sync to Device</span>
            </Trans>
          }
          size='small'
          outline
          onClick={() => {
            if (!isInDesktopMode()) {
              openInElectronApp(
                'connectOnProjectPage',
                routerStore.route.path,
                () => {
                  setDownloadDialogVisible(true);
                },
              );
            }
          }}
        />

        <input
          type='file'
          ref={ref => {
            this.folderInput = ref;
          }}
          multiple
          onChange={runOnChange}
          directory='true'
          webkitdirectory='true'
          mozdirectory='true'
          allowdirs=''
          id='upload_folder'
          style={{ display: 'none' }}
        ></input>
        <InputFileStyled
          type='file'
          ref={ref => {
            this.fileInput = ref;
          }}
          id='upload_file'
          multiple={true}
          onChange={this.addFiles}
        />
      </ButtonsWrapper>
    );

    return (
      <>
        {isInDesktopMode() ? (
          <SyncProjectContainer repoManager={repoManager} />
        ) : (
          <Section
            title={t(title)}
            subTitle={subTitle}
            headerWrapperStyle={{
              borderBottom: `1px solid ${themes.color.wfGrey100}`,
              paddingBottom: 18,
            }}
          >
            {crumbs}
            {Buttons}
            <FilesTab
              condition={!hasError}
              app={repository}
              project={project}
              manager={repoManager}
              returnLink={returnLink}
            />
            {hasError && (
              <Center>
                <Trans>
                  Ooops, an error related to the repository has been detected.
                </Trans>
              </Center>
            )}
          </Section>
        )}
        {showHistory && (
          <HistorySection
            app={repository}
            project={project}
            manager={repoManager}
          />
        )}
      </>
    );
  }
}
